import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useSEO, buildArticleLd } from "@/lib/seo";

// Tiny markdown renderer — h1-h3, bold/italic, links, lists, paragraphs
function renderMD(md = "") {
  const lines = md.split("\n");
  const html = [];
  let inList = false;

  const inline = (s) =>
    s
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\*\*(.+?)\*\*/g, '<strong class="text-white font-bold">$1</strong>')
      .replace(/\*(.+?)\*/g, '<em class="italic text-white/85">$1</em>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-arre-red underline" target="_blank" rel="noreferrer">$1</a>');

  for (const raw of lines) {
    const l = raw.trimEnd();
    if (l.startsWith("### ")) { if (inList) { html.push("</ul>"); inList = false; } html.push(`<h3 class="font-display text-2xl text-white tracking-wider uppercase mt-7 mb-3">${inline(l.slice(4))}</h3>`); continue; }
    if (l.startsWith("## ")) { if (inList) { html.push("</ul>"); inList = false; } html.push(`<h2 class="font-display text-3xl text-white tracking-wider uppercase mt-10 mb-3">${inline(l.slice(3))}</h2>`); continue; }
    if (l.startsWith("# ")) { if (inList) { html.push("</ul>"); inList = false; } html.push(`<h1 class="font-display text-4xl text-white tracking-wider uppercase mt-10 mb-4">${inline(l.slice(2))}</h1>`); continue; }
    if (l.startsWith("- ") || l.startsWith("* ")) { if (!inList) { html.push('<ul class="list-disc ml-5 space-y-1.5 my-3 text-white/85">'); inList = true; } html.push(`<li>${inline(l.slice(2))}</li>`); continue; }
    if (l === "") { if (inList) { html.push("</ul>"); inList = false; } else html.push(""); continue; }
    if (inList) { html.push("</ul>"); inList = false; }
    html.push(`<p class="text-white/85 leading-relaxed my-3">${inline(l)}</p>`);
  }
  if (inList) html.push("</ul>");
  return html.join("\n");
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false);
    api.get(`/blog/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  useSEO(
    post
      ? {
          title: post.seo_title || post.title,
          description: post.seo_description || post.excerpt,
          image: post.cover_image,
          type: "article",
          jsonLd: buildArticleLd(post),
        }
      : { title: "Cargando..." }
  );

  if (loading) return <div className="bg-arre-black min-h-screen flex items-center justify-center"><div className="font-display text-2xl text-white/50 tracking-wider">Cargando...</div></div>;
  if (notFound) return (
    <div className="bg-arre-black min-h-screen flex items-center justify-center px-6">
      <div className="text-center">
        <h1 className="font-display text-5xl text-white tracking-wider uppercase mb-4">Artículo no encontrado</h1>
        <Link to="/blog" className="text-arre-red text-xs tracking-widest uppercase hover:underline">← Volver al blog</Link>
      </div>
    </div>
  );

  return (
    <article className="bg-arre-black min-h-screen" data-testid="blog-post-page">
      {post.cover_image && (
        <div className="relative h-[50vh] md:h-[65vh] overflow-hidden">
          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-arre-black/30 to-arre-black" />
        </div>
      )}
      <div className="max-w-3xl mx-auto px-6 -mt-32 md:-mt-40 relative z-10 pb-20">
        <Link to="/blog" className="inline-flex items-center gap-1 text-arre-red text-xs tracking-widest uppercase hover:underline mb-5">
          <ArrowLeft className="w-3 h-3" /> Blog
        </Link>
        <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-3">⟡ {post.category}</div>
        <h1 className="font-display text-4xl md:text-6xl tracking-wider uppercase text-white leading-[0.95]" data-testid="blog-post-title">
          {post.title}
        </h1>
        <div className="flex items-center gap-3 mt-5 text-[11px] text-white/50 tracking-widest uppercase">
          <Calendar className="w-3 h-3" />
          {new Date(post.created_at).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" })}
          {post.views > 0 && <span>· {post.views} lecturas</span>}
        </div>
        <div className="font-serif-display italic text-xl text-white/75 mt-6 mb-10 border-l-2 border-arre-red pl-5">
          {post.excerpt}
        </div>
        <div className="prose-arre" dangerouslySetInnerHTML={{ __html: renderMD(post.body_md) }} />
        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-10 pt-6 border-t border-arre-border">
            {post.tags.map((t) => (
              <span key={t} className="text-xs bg-arre-surface border border-arre-border text-white/70 px-3 py-1">#{t}</span>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}
